import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/endpoints';
import type { ReportOverview } from '../api/types';
import { BarChart, Legend, NEGATIVE, POSITIVE, RankedBars, SERIES, TipRow } from '../components/charts';
import { Card, EmptyState, ErrorNote, Input, Spinner } from '../components/ui';
import { formatMoney } from '../lib/format';

// Dates are the cafe's calendar days, YYYY-MM-DD, built from local parts —
// toISOString() would hand back yesterday for the first hours of an IST day.
function isoDay(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return isoDay(date);
}
function shortDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' });
}
function longDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

// Axis labels: ₹12k rather than ₹12,000 so the y-axis stays narrow.
function compactMoney(value: number): string {
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(abs % 100000 ? 1 : 0)}L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(abs % 1000 ? 1 : 0)}k`;
  return `${sign}₹${Math.round(abs)}`;
}
const whole = (value: number) => String(Math.round(value));

const PRESETS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
] as const;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const REVENUE_SERIES = [
  { key: 'revenue_recharge', name: 'Recharges', color: SERIES[0] },
  { key: 'revenue_fnb', name: 'Food & drinks', color: SERIES[1] },
  { key: 'revenue_ps5', name: 'PS5', color: SERIES[2] },
] as const;

function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'good' | 'bad';
}) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === 'good' ? 'text-emerald-300' : tone === 'bad' ? 'text-rose-300' : 'text-slate-100'
        }`}
      >
        {value}
      </p>
      {note && <p className="mt-0.5 text-xs text-slate-500">{note}</p>}
    </Card>
  );
}

function ChartCard({
  title,
  subtitle,
  legend,
  children,
}: {
  title: string;
  subtitle?: string;
  legend?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-200">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        {legend}
      </div>
      {children}
    </Card>
  );
}

// The owner's view: who came in, what they spent, what it cost to stay open.
// Admin only — the backend refuses staff tokens too.
export function ReportsPage() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState<ReportOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!from || !to) return;
    const controller = new AbortController();
    setLoading(true);
    api.reports
      .overview({ from, to }, controller.signal)
      .then((result) => {
        setData(result);
        setError(null);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [from, to]);

  // Average footfall per weekday over the range — "which days are busy?" —
  // averaged over how many of each weekday the range contains, so a range
  // with five Saturdays and four Mondays is still a fair comparison.
  const weekdays = useMemo(() => {
    const buckets = WEEKDAYS.map(() => ({ customers: 0, days: 0 }));
    for (const day of data?.daily ?? []) {
      const index = (new Date(`${day.date}T00:00:00`).getDay() + 6) % 7;
      buckets[index].customers += day.customers;
      buckets[index].days += 1;
    }
    return buckets.map((bucket, index) => ({
      name: WEEKDAYS[index],
      average: bucket.days ? bucket.customers / bucket.days : 0,
      days: bucket.days,
    }));
  }, [data]);

  const activePreset = PRESETS.find((preset) => from === daysAgo(preset.days - 1) && to === daysAgo(0));
  const totals = data?.totals;
  const quiet = data && data.totals.visits === 0 && data.totals.revenue === 0 && data.totals.expenses === 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-lg font-semibold">Reports</h1>
        {/* Filters in one row, above everything they control. */}
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex rounded-lg border border-slate-700 bg-slate-900 p-1">
            {PRESETS.map((preset) => (
              <button
                key={preset.days}
                type="button"
                onClick={() => {
                  setFrom(daysAgo(preset.days - 1));
                  setTo(daysAgo(0));
                }}
                className={`min-h-9 rounded-md px-3 text-sm font-medium transition-colors ${
                  activePreset === preset
                    ? 'bg-sky-500 text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="w-40">
            <Input label="From" type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} />
          </div>
          <div className="w-40">
            <Input label="To" type="date" value={to} min={from} max={daysAgo(0)} onChange={(event) => setTo(event.target.value)} />
          </div>
        </div>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {loading && !data ? (
        <Spinner label="Building the report…" />
      ) : !data || !totals ? null : (
        <div className={`space-y-4 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Stat
              label="Revenue"
              value={formatMoney(totals.revenue)}
              note={
                `${formatMoney(totals.collected)} collected · ${formatMoney(totals.cash_collected)} cash, ${formatMoney(totals.upi_collected)} UPI` +
                ((totals.refunds ?? 0) > 0 ? ` · after ${formatMoney(totals.refunds ?? 0)} pack refunds` : '')
              }
            />
            <Stat
              label="Expenses"
              value={formatMoney(totals.expenses)}
              note={`${data.expenses_by_category.length} categor${data.expenses_by_category.length === 1 ? 'y' : 'ies'}`}
            />
            <Stat
              label="Net"
              value={`${totals.net < 0 ? '−' : ''}${formatMoney(Math.abs(totals.net))}`}
              note="Revenue less expenses"
              tone={totals.net > 0 ? 'good' : totals.net < 0 ? 'bad' : undefined}
            />
            <Stat
              label="Customers"
              value={String(totals.customers)}
              note={`${totals.visits} visits · ${totals.new_customers} new`}
            />
          </div>

          {quiet ? (
            <EmptyState title="Nothing happened in these dates.">
              No sessions, sales or expenses between {longDay(data.from)} and {longDay(data.to)}.
            </EmptyState>
          ) : (
            <>
              <ChartCard title="Footfall" subtitle="Customers who played each day">
                <BarChart
                  ariaLabel="Customers per day"
                  formatValue={whole}
                  integer
                  columns={data.daily.map((day) => ({
                    key: day.date,
                    label: shortDay(day.date),
                    segments: [{ name: 'Customers', value: day.customers, color: SERIES[0] }],
                    tooltip: (
                      <>
                        <p className="mb-1 font-medium text-slate-100">{longDay(day.date)}</p>
                        <TipRow name="Customers" value={String(day.customers)} />
                        <TipRow name="Visits" value={String(day.visits)} />
                      </>
                    ),
                  }))}
                />
              </ChartCard>

              <ChartCard
                title="Revenue"
                subtitle="Sales settled each day, at selling price"
                legend={<Legend items={REVENUE_SERIES.map(({ name, color }) => ({ name, color }))} />}
              >
                <BarChart
                  ariaLabel="Revenue per day by recharges, food and drinks, and PS5"
                  formatValue={compactMoney}
                  columns={data.daily.map((day) => ({
                    key: day.date,
                    label: shortDay(day.date),
                    segments: REVENUE_SERIES.map((series) => ({
                      name: series.name,
                      value: day[series.key],
                      color: series.color,
                    })),
                    tooltip: (
                      <>
                        <p className="mb-1 font-medium text-slate-100">{longDay(day.date)}</p>
                        {REVENUE_SERIES.map((series) => (
                          <TipRow
                            key={series.key}
                            color={series.color}
                            name={series.name}
                            value={formatMoney(day[series.key])}
                          />
                        ))}
                        <div className="mt-1 border-t border-slate-800 pt-1">
                          <TipRow name="Total" value={formatMoney(day.revenue)} />
                        </div>
                      </>
                    ),
                  }))}
                />
              </ChartCard>

              <ChartCard
                title="Net by day"
                subtitle="Revenue less expenses — below the line is a loss"
                legend={
                  <Legend
                    items={[
                      { name: 'Profit', color: POSITIVE },
                      { name: 'Loss', color: NEGATIVE },
                    ]}
                  />
                }
              >
                <BarChart
                  ariaLabel="Net revenue less expenses per day"
                  formatValue={compactMoney}
                  columns={data.daily.map((day) => {
                    const net = day.revenue - day.expenses;
                    return {
                      key: day.date,
                      label: shortDay(day.date),
                      segments: [{ name: 'Net', value: net, color: net < 0 ? NEGATIVE : POSITIVE }],
                      tooltip: (
                        <>
                          <p className="mb-1 font-medium text-slate-100">{longDay(day.date)}</p>
                          <TipRow name="Revenue" value={formatMoney(day.revenue)} />
                          <TipRow name="Expenses" value={formatMoney(day.expenses)} />
                          <div className="mt-1 border-t border-slate-800 pt-1">
                            <TipRow name="Net" value={`${net < 0 ? '−' : ''}${formatMoney(Math.abs(net))}`} />
                          </div>
                        </>
                      ),
                    };
                  })}
                />
              </ChartCard>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ChartCard title="Busy hours" subtitle="Sessions started in each hour, whole range">
                  <BarChart
                    ariaLabel="Sessions started per hour of day"
                    height={200}
                    formatValue={whole}
                    integer
                    columns={data.hourly.map((row) => ({
                      key: String(row.hour),
                      label: `${row.hour}`,
                      segments: [{ name: 'Visits', value: row.visits, color: SERIES[0] }],
                      tooltip: (
                        <>
                          <p className="mb-1 font-medium text-slate-100">
                            {String(row.hour).padStart(2, '0')}:00 – {String((row.hour + 1) % 24).padStart(2, '0')}:00
                          </p>
                          <TipRow name="Sessions started" value={String(row.visits)} />
                        </>
                      ),
                    }))}
                  />
                </ChartCard>

                <ChartCard title="Busy days" subtitle="Average customers per weekday">
                  <BarChart
                    ariaLabel="Average customers per weekday"
                    height={200}
                    formatValue={(value) => (value % 1 ? value.toFixed(1) : String(value))}
                    columns={weekdays.map((row) => ({
                      key: row.name,
                      label: row.name,
                      segments: [{ name: 'Average', value: row.average, color: SERIES[0] }],
                      tooltip: (
                        <>
                          <p className="mb-1 font-medium text-slate-100">{row.name}</p>
                          <TipRow name="Avg customers" value={row.average.toFixed(1)} />
                          <TipRow name="Days in range" value={String(row.days)} />
                        </>
                      ),
                    }))}
                  />
                </ChartCard>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ChartCard title="Where the money came from">
                  <RankedBars
                    formatValue={formatMoney}
                    rows={REVENUE_SERIES.map((series) => ({
                      name: series.name,
                      value: totals[series.key],
                      note: totals.revenue ? `${Math.round((totals[series.key] / totals.revenue) * 100)}%` : undefined,
                    }))}
                  />
                  {totals.revenue_fnb > 0 && (
                    <p className="text-xs text-slate-500">
                      Food & drinks cost {formatMoney(totals.fnb_cost)} to buy — a margin of{' '}
                      {formatMoney(totals.revenue_fnb - totals.fnb_cost)}. Food from commission vendors counts
                      only the cafe’s cut, so collected runs higher than revenue by the kitchens’ share.
                    </p>
                  )}
                  {(totals.staff_fnb ?? 0) > 0 && (
                    <p className="text-xs text-slate-500">
                      Staff tabs settled at cost: {formatMoney(totals.staff_fnb ?? 0)} — counted in collected, not in
                      revenue.
                    </p>
                  )}
                </ChartCard>

                <ChartCard title="Where the money went" subtitle="Expenses by category">
                  {data.expenses_by_category.length === 0 ? (
                    <p className="text-sm text-slate-500">No expenses recorded in these dates.</p>
                  ) : (
                    <RankedBars
                      formatValue={formatMoney}
                      rows={data.expenses_by_category.map((row) => ({
                        name: row.category,
                        value: row.amount,
                        note: `×${row.count}`,
                      }))}
                    />
                  )}
                </ChartCard>
              </div>

              <ChartCard
                title="Top customers"
                subtitle="Highest paying in these dates — settled sales, less pack refunds"
                legend={<Legend items={REVENUE_SERIES.map(({ name, color }) => ({ name, color }))} />}
              >
                {!data.top_customers || data.top_customers.length === 0 ? (
                  <p className="text-sm text-slate-500">Nobody paid for anything in these dates.</p>
                ) : (
                  <ol className="divide-y divide-slate-800">
                    {data.top_customers.map((row, index) => {
                      const top = data.top_customers![0].total || 1;
                      const parts = [
                        { name: 'Recharges', value: row.recharge, color: SERIES[0] },
                        { name: 'Food & drinks', value: row.fnb, color: SERIES[1] },
                        { name: 'PS5', value: row.ps5, color: SERIES[2] },
                      ];
                      return (
                        <li key={row.customer_id} className="space-y-1.5 py-2.5">
                          <div className="flex items-baseline justify-between gap-3">
                            <Link
                              to={`/customers/${row.customer_id}`}
                              className="min-w-0 truncate text-sm text-slate-200 hover:text-sky-300"
                            >
                              <span className="mr-2 text-slate-500">{index + 1}.</span>
                              {row.name}
                              <span className="ml-2 text-xs text-slate-500">{row.phone}</span>
                            </Link>
                            <span className="shrink-0 font-semibold text-slate-100">
                              {formatMoney(row.total)}
                            </span>
                          </div>
                          {/* Bar length is against the top spender, split by kind. */}
                          <div
                            className="flex h-2 overflow-hidden rounded-full bg-slate-800"
                            style={{ width: `${Math.max((row.total / top) * 100, 2)}%` }}
                            aria-hidden
                          >
                            {parts.map((part) =>
                              part.value > 0 ? (
                                <span
                                  key={part.name}
                                  style={{ flexGrow: part.value, backgroundColor: part.color }}
                                />
                              ) : null,
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            {parts
                              .filter((part) => part.value !== 0)
                              .map((part) => `${part.name} ${formatMoney(part.value)}`)
                              .join(' · ')}
                          </p>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </ChartCard>

              {/* The same numbers as a table: for reading exact figures, and
                  for anyone who cannot use the charts. */}
              <details className="rounded-xl border border-slate-800 px-4 py-3">
                <summary className="cursor-pointer text-sm text-slate-400">Show day by day as a table</summary>
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3 text-right">Customers</th>
                        <th className="py-2 pr-3 text-right">Visits</th>
                        <th className="py-2 pr-3 text-right">Recharges</th>
                        <th className="py-2 pr-3 text-right">Food & drinks</th>
                        <th className="py-2 pr-3 text-right">PS5</th>
                        <th className="py-2 pr-3 text-right">Revenue</th>
                        <th className="py-2 pr-3 text-right">Expenses</th>
                        <th className="py-2 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 tabular-nums">
                      {[...data.daily].reverse().map((day) => {
                        const net = day.revenue - day.expenses;
                        return (
                          <tr key={day.date}>
                            <td className="py-2 pr-3 text-slate-300">{longDay(day.date)}</td>
                            <td className="py-2 pr-3 text-right">{day.customers}</td>
                            <td className="py-2 pr-3 text-right">{day.visits}</td>
                            <td className="py-2 pr-3 text-right">{formatMoney(day.revenue_recharge)}</td>
                            <td className="py-2 pr-3 text-right">{formatMoney(day.revenue_fnb)}</td>
                            <td className="py-2 pr-3 text-right">{formatMoney(day.revenue_ps5)}</td>
                            <td className="py-2 pr-3 text-right text-slate-100">{formatMoney(day.revenue)}</td>
                            <td className="py-2 pr-3 text-right">{formatMoney(day.expenses)}</td>
                            <td className={`py-2 text-right ${net < 0 ? 'text-rose-300' : 'text-slate-100'}`}>
                              {net < 0 ? '−' : ''}
                              {formatMoney(Math.abs(net))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </details>
            </>
          )}
        </div>
      )}
    </div>
  );
}
